<template>
  <v-col cols="12" md="12" class="py-0">
    <div class="pb-2">
      <p class="ma-0 pa-0 pb-2 caption">
        {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.source_label") }}
      </p>
      <div class="d-flex upload-source-toggle" style="gap: 12px">
        <v-btn
          :color="normalizedSource === 'local' ? 'primary' : undefined"
          :outlined="normalizedSource !== 'local'"
          small
          @click="onSourceChange('local')"
        >
          <v-icon small left>mdi-upload</v-icon>
          {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.local_btn") }}
        </v-btn>
        <v-btn
          :color="normalizedSource === 'cloud' ? 'primary' : undefined"
          :outlined="normalizedSource !== 'cloud'"
          small
          @click="onSourceChange('cloud')"
        >
          <v-icon small left>mdi-cloud-upload-outline</v-icon>
          {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.cloud_btn") }}
        </v-btn>
      </div>
    </div>

    <div v-if="normalizedSource === 'cloud'" class="pt-2">
      <p class="ma-0 pa-0 pb-3 caption">
        {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.provider_label") }}
      </p>
      <div class="d-flex flex-wrap" style="gap: 12px">
        <v-card
          v-for="p in cloudProviders"
          :key="p.value"
          outlined
          class="cloud-provider-card px-4 d-flex align-center"
          :class="{
            'cloud-provider-card--selected': selectedProvider === p.value || hasItems(p.value),
          }"
          style="width: 220px; height: 56px; cursor: pointer"
          @click="$emit('open-picker', p.value)"
        >
          <img v-if="p.logo" :src="p.logo" width="24" height="24" class="mr-3" />
          <v-icon v-else size="24" :color="p.color" class="mr-3">{{ p.icon }}</v-icon>
          <span class="font-weight-medium">{{ p.label }}</span>
          <v-spacer />
          <v-icon v-if="hasItems(p.value)" color="primary" small> mdi-check-circle </v-icon>
          <v-icon v-else color="grey lighten-1" small>mdi-chevron-right</v-icon>
        </v-card>
      </div>

      <div v-if="allCloudItems.length" class="mt-3">
        <div v-for="cs in cloudSourceMap" :key="cs.provider" class="mb-3">
          <div class="d-flex align-center mb-1" style="gap: 4px">
            <img
              v-if="providerConfig(cs.provider).logo"
              :src="providerConfig(cs.provider).logo"
              width="18"
              height="18"
            />
            <v-icon v-else small :color="providerConfig(cs.provider).color">
              {{ providerConfig(cs.provider).icon }}
            </v-icon>
            <span class="body-2 font-weight-medium">
              {{
                $vuetify.lang
                  .t("$vuetify.gpt_dataset.cloud_storage.selected_files_from")
                  .replace("{provider}", providerConfig(cs.provider).label)
              }}
            </span>
            <v-spacer />
            <v-btn small text color="primary" class="text-none" @click="$emit('clear-provider', cs.provider)">
              {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.clear_all") }}
            </v-btn>
            <v-btn small text color="primary" class="text-none" @click="$emit('open-picker', cs.provider)">
              <v-icon small left>mdi-plus-circle-outline</v-icon>
              {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.add_more") }}
            </v-btn>
          </div>
          <div class="d-flex flex-wrap" style="gap: 6px">
            <v-chip
              v-for="item in cs.items"
              :key="item.id"
              small
              label
              color="blue lighten-5"
              close
              close-icon="mdi-close"
              class="cloud-file-chip"
              :title="item.name"
              @click:close="$emit('remove-item', { provider: cs.provider, itemId: item.id })"
            >
              <v-icon small left :color="item.is_folder ? 'amber darken-2' : getCloudFileColor(item.mime_type)">
                {{ item.is_folder ? "mdi-folder-outline" : getCloudFileIcon(item.mime_type) }}
              </v-icon>
              <span class="text-truncate">{{ item.name }}</span>
            </v-chip>
          </div>
        </div>
      </div>

      <p v-else class="caption grey--text mt-2">
        {{ $vuetify.lang.t("$vuetify.gpt_dataset.cloud_storage.click_to_browse") }}
      </p>
    </div>
  </v-col>
</template>

<script>
import { getCloudFileIcon, getCloudFileColor } from "@/helpers/helper";
import { CLOUD_PROVIDERS } from "../../../mixins/datasetMixin";

export default {
  name: "UploadSourceSelector",
  props: {
    // 'local' | 'cloud'
    source: { type: String, default: "local" },
    // [{ provider, account_id, items: [...] }]
    cloudSources: { type: Array, default: () => [] },
    // provider whose card should render as selected
    selectedProvider: { type: String, default: "" },
  },
  computed: {
    cloudProviders() {
      return CLOUD_PROVIDERS;
    },
    normalizedSource() {
      return this.source === "cloud" ? "cloud" : "local";
    },
    cloudSourceMap() {
      const map = {};
      (this.cloudSources || []).forEach(s => {
        const existing = map[s.provider]?.items || [];
        map[s.provider] = { ...s, items: [...existing, ...(s.items || [])] };
      });
      return map;
    },
    allCloudItems() {
      return (this.cloudSources || []).flatMap(s => s.items || []);
    },
  },
  methods: {
    providerConfig(provider) {
      return CLOUD_PROVIDERS.find(p => p.value === provider) || {};
    },
    hasItems(provider) {
      return !!this.cloudSourceMap[provider]?.items?.length;
    },
    getCloudFileIcon,
    getCloudFileColor,
    onSourceChange(value) {
      const next = value === "cloud" ? "cloud" : "local";
      if (next === this.normalizedSource) return;
      this.$emit("change", next);
    },
  },
};
</script>

<style scoped>
.upload-source-toggle .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
@media (max-width: 350px) {
  .upload-source-toggle {
    flex-direction: column;
  }
  .upload-source-toggle .v-btn {
    margin-left: 0 !important;
  }
}
.cloud-provider-card {
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
}
.cloud-provider-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12) !important;
}
.cloud-file-chip {
  max-width: 100%;
}
.cloud-file-chip >>> .v-chip__content {
  min-width: 0;
  max-width: 100%;
}
.cloud-file-chip >>> .v-icon {
  flex-shrink: 0;
}
.cloud-provider-card--selected {
  /* Fallback required: the theme has no customProperties, so --v-primary-base
     is undefined and the bare var() dropped the declaration. */
  border-color: var(--v-primary-base, #004aad) !important;
  border-width: 2px;
  background-color: rgba(0, 74, 173, 0.03);
}
</style>
